/**
 * Handmatige post (`/posting/create`) — gemeten aan oud.
 *
 * Velden zoals het oude formulier: Model, Audience ("Who can see this post"),
 * Post Title / Caption, Description, Tags (min 2 required, pipe-separated),
 * Media met de twee knoppen Free Preview en Main Content, Platform, Post
 * Type, Publication Date & Time en Expiration (optional).
 *
 * Deze pagina maakt één post klaar; de posting-bot blijft er buiten. Er wordt
 * niets verstuurd zonder dat de gebruiker op Save drukt en bevestigt.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Panel, Plat } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import type { Persona } from './Models'

const AUDIENCES = [
  { value: 'public', label: 'Public (everyone)' },
  { value: 'followers', label: 'Followers only' },
  { value: 'subscribers', label: 'Subscribers only' },
]
const POST_TYPES = [
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
  { value: 'text', label: 'Text only' },
]

export default function PostCreate() {
  const navigate = useNavigate()
  const { run, busy } = useActions()
  const personas = useApi<Persona[]>('/api/admin/models/personas?split=true')

  const [model, setModel] = useState('')
  const [audience, setAudience] = useState('public')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [platform, setPlatform] = useState('fancentro')
  const [postType, setPostType] = useState('photo')
  const [publishAt, setPublishAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [preview, setPreview] = useState<File | null>(null)
  const [main, setMain] = useState<File | null>(null)

  /** Oud eist minstens twee pipe-gescheiden tags. */
  const tagList = tags.split('|').map((t) => t.trim()).filter(Boolean)
  const tagsOk = tagList.length >= 2
  const ready = !!model && !!title.trim() && tagsOk && !!main

  async function submit() {
    const fd = new FormData()
    fd.append('model_persona_id', model)
    fd.append('audience', audience)
    fd.append('title', title)
    fd.append('description', description)
    fd.append('tags', tagList.join('|'))
    fd.append('platform', platform)
    fd.append('post_type', postType)
    if (publishAt) fd.append('publish_at', publishAt)
    if (expiresAt) fd.append('expires_at', expiresAt)
    if (preview) fd.append('preview', preview)
    if (main) fd.append('media', main)
    const r = await fetch('/api/admin/posting/manual-post', {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_access_token') || ''}` },
      body: fd,
    })
    if (!r.ok) {
      throw new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`)
    }
    return r.json().catch(() => null)
  }

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={() => navigate('/posting')}>
          Posting Bot
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        <button
          className="btn primary sm"
          type="button"
          disabled={!ready || busy === 'Create post'}
          onClick={() => run({
            label: 'Create post',
            confirm: `Prepare this post now for ${
              personas.data?.find((p) => String(p.id) === model)?.stage_name ?? 'this model'}?`,
            request: submit,
            onDone: () => navigate('/posting'),
            successText: 'Post created.',
          })}
        >
          Save
        </button>
      </div>

      <Panel title="Create Manual Post">
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>Model</label>
            <select className="inp" value={model} onChange={(e) => setModel(e.target.value)}>
              <option value="">Choose a model…</option>
              {(personas.data ?? []).map((p) => (
                <option key={p.id} value={String(p.id)}>{p.stage_name || p.slug}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Audience</label>
            <select className="inp" value={audience} onChange={(e) => setAudience(e.target.value)}>
              {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <span className="hint">Who can see this post</span>
          </div>
        </div>

        <div className="field">
          <label>Post Title / Caption</label>
          <input className="inp" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea className="inp" rows={4} value={description}
                    onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="field">
          <label>Tags (min 2 required)</label>
          <input className="inp" value={tags} onChange={(e) => setTags(e.target.value)}
                 placeholder="tag one | tag two" />
          <span className="hint">
            Pipe-separated{tagsOk ? '' : ' — at least two tags needed'}
          </span>
        </div>
      </Panel>

      <Panel title="Media">
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <label className="btn ghost sm" style={{ cursor: 'pointer' }}>
            {preview ? preview.name.slice(0, 26) : 'Free Preview'}
            <input type="file" style={{ display: 'none' }}
                   onChange={(e) => setPreview(e.target.files?.[0] ?? null)} />
          </label>
          {preview ? (
            <button className="btn ghost sm" type="button" onClick={() => setPreview(null)}>
              Remove preview
            </button>
          ) : null}
          <label className="btn ghost sm" style={{ cursor: 'pointer' }}>
            {main ? main.name.slice(0, 26) : 'Main Content'}
            <input type="file" style={{ display: 'none' }}
                   onChange={(e) => setMain(e.target.files?.[0] ?? null)} />
          </label>
          {main ? (
            <button className="btn ghost sm" type="button" onClick={() => setMain(null)}>
              Remove media
            </button>
          ) : null}
        </div>
        <span className="hint">
          The preview is what non-paying fans see; Main Content is the file
          that gets posted.
        </span>
      </Panel>

      <Panel title="Publication">
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>Platform</label>
            <select className="inp" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              <option value="fancentro">FanCentro</option>
              <option value="f2f">F2F</option>
            </select>
            <span className="hint"><Plat value={platform} /></span>
          </div>
          <div className="field">
            <label>Post Type</label>
            <select className="inp" value={postType} onChange={(e) => setPostType(e.target.value)}>
              {POST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Publication Date &amp; Time</label>
            <input className="inp" type="datetime-local" value={publishAt}
                   onChange={(e) => setPublishAt(e.target.value)} />
          </div>
          <div className="field">
            <label>Expiration (optional)</label>
            <input className="inp" type="datetime-local" value={expiresAt}
                   onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>
      </Panel>
    </>
  )
}
